import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type Pasta } from './api';
import { chaves } from './consultas';
import { useUi } from '../store/ui';
import { descreverAdiamento, horarioDeAdiamento, type QuandoAdiar } from './adiar';

/**
 * Lembretes de acompanhamento (WYM-15, ADR 0061). O horario reaproveita as
 * opcoes do adiar; o servidor guarda o instante em UTC e o fuso so para exibir.
 */

export interface LembreteDaApi {
  id: string;
  accountId: string;
  messageId: string;
  subject: string;
  dueAt: string;
  timezone: string;
  status: 'pending' | 'fired';
}

export interface AguardandoDaApi {
  messageId: string;
  accountId: string;
  subject: string;
  sentAt: string;
  days: number;
}

export interface Lembretes {
  items: LembreteDaApi[];
  awaiting: AguardandoDaApi[];
}

export const CHAVE_LEMBRETES = ['lembretes'] as const;

export function useLembretes() {
  return useQuery({
    queryKey: CHAVE_LEMBRETES,
    queryFn: () => api<Lembretes>('/reminders'),
    staleTime: 60_000,
  });
}

export function useMarcarLembrete() {
  const cliente = useQueryClient();
  return useMutation<{ id: string }, Error, { messageId: string; quando: QuandoAdiar }>({
    mutationFn: ({ messageId, quando }) =>
      api<{ id: string }>('/reminders', {
        method: 'POST',
        body: {
          messageId,
          dueAt: horarioDeAdiamento(quando).toISOString(),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: CHAVE_LEMBRETES }),
  });
}

export function useCancelarLembrete() {
  const cliente = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => api<void>(`/reminders/${id}`, { method: 'DELETE' }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: CHAVE_LEMBRETES }),
  });
}

/** Texto da confirmacao ao marcar. */
export function textoDoLembrete(quando: QuandoAdiar): string {
  return `Acompanhar em ${descreverAdiamento(horarioDeAdiamento(quando))}, se não houver resposta.`;
}

/** Vencimento no fuso em que foi marcado. */
export function descreverVencimento(iso: string, fuso: string): string {
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      timeZone: fuso,
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return new Date(iso).toLocaleString('pt-BR');
  }
}

/**
 * Abre a mensagem de um lembrete ou de "Aguardando resposta". Se ela e de outra
 * caixa, troca a caixa e a pasta (Enviados) antes: sem isso o painel mostrava a
 * mensagem de B com a barra e a lista em A.
 */
export function useAbrirAcompanhado() {
  const queryClient = useQueryClient();
  return async (accountId: string, messageId: string) => {
    const ui = useUi.getState();
    if (ui.accountId !== accountId) {
      const pastas = await queryClient
        .fetchQuery({
          queryKey: chaves.pastas(accountId),
          queryFn: () => api<Pasta[]>(`/accounts/${accountId}/folders`),
          staleTime: 60_000,
        })
        .catch(() => [] as Pasta[]);
      ui.selectAccount(accountId);
      const enviados = pastas.find((p) => p.role === 'sent');
      if (enviados !== undefined) ui.selectFolder(enviados.id);
    }
    const atual = useUi.getState();
    atual.setFilter('all');
    atual.setQuery('');
    atual.setModulo('mail');
    atual.selectMensagem(messageId);
  };
}
